import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, MapPin, Navigation, Coffee, Instagram, Facebook } from 'lucide-react';
import { AlmondIcon } from '../components/AlmondIcon';
import useReveal from '../hooks/useReveal';
import { cafe, images } from '../mock/mock';
import { useContent } from '../context/ContentContext';

export default function Cafe() {
  useReveal([]);
  const { img, content } = useContent();
  const c = content.cafe || {};
  const gallery = [
    img('cafeInterior', images.cafeInterior),
    img('cafeTable', images.cafeTable),
    img('cafeCroissant', images.cafeCroissant),
    img('cafeStorefront', images.cafeStorefront),
  ];

  return (
    <div className="bg-[#E9EBED]">
      <div className="pt-28" />
      {/* hero, hidden; remove the spacer above and "hidden" below to reinstate */}
      <section className="hidden relative pt-44 pb-16 min-h-[480px] flex items-center overflow-hidden bg-[#E9E7E2] text-[#2C3A28]">
        <img
          src={cafe.hero}
          alt="Al Naturale Cafe storefront in St Kilda"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#E9E7E2] via-[#E9E7E2]/70 to-[#E9E7E2]/40" />
        <div className="relative w-full max-w-7xl mx-auto px-5 sm:px-8">
          <span className="text-[#2C3A28] text-sm font-semibold uppercase tracking-[0.2em]">
            Our café
          </span>
          <h1 className="mt-4 text-6xl sm:text-7xl text-[#2C3A28] max-w-2xl leading-[1.05]" style={{ fontFamily: "'Pinyon Script', cursive" }}>
            {cafe.welcome}
          </h1>
          <div className="mt-6 flex flex-wrap gap-x-8 gap-y-3 text-[#2C3A28]">
            <span className="inline-flex items-center gap-2 text-lg">
              <Clock size={19} className="text-[#2C3A28]" /> {cafe.hours}
            </span>
            <span className="inline-flex items-center gap-2 text-lg">
              <MapPin size={19} className="text-[#2C3A28]" /> {cafe.address}
            </span>
          </div>
        </div>
      </section>

      {/* intro */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1.3fr_1fr] gap-12 items-center">
          <div className="reveal">
            <span className="inline-flex items-center gap-2 text-[#2C3A28] text-sm font-semibold uppercase tracking-[0.2em]">
              <AlmondIcon size={15} className="text-[#D2B489]" /> {c.eyebrow || cafe.note}
            </span>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-semibold uppercase text-[#2C3A28] leading-tight">
              {c.heading || 'A genuine, healthy & delicious experience'}
            </h2>
            <p className="mt-6 text-[#3B2A20]/80 text-lg leading-relaxed">{c.body || cafe.body}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/menu"
                className="inline-flex items-center gap-2 rounded-full bg-[#2C3A28] text-white px-7 py-4 font-semibold hover:bg-[#1f2a1c] transition-all hover:-translate-y-0.5"
              >
                <Coffee size={18} /> View our menu
              </Link>
              <a
                href="https://www.google.com/maps/search/?api=1&query=Al+Naturale+6+Acland+Street+St+Kilda"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-[#2C3A28]/25 text-[#2C3A28] px-7 py-4 font-semibold hover:bg-[#EDEAE0] transition-colors"
              >
                <Navigation size={18} /> Get directions
              </a>
            </div>
          </div>

          <div className="reveal grid grid-cols-2 gap-4">
            <div className="rounded-2xl bg-[#2C3A28] text-[#EDEAE0] p-6">
              <Clock size={24} className="text-[#2C3A28]" />
              <p className="mt-3 font-display text-xl text-[#E9EBED]">Open daily</p>
              <p className="text-sm text-[#EDEAE0]/70">7am to 3pm</p>
            </div>
            <div className="rounded-2xl bg-[#2C3A28] text-[#E9EBED] p-6">
              <Coffee size={24} />
              <p className="mt-3 font-display text-xl">House milks</p>
              <p className="text-sm text-[#E9EBED]/80">In every cup</p>
            </div>
            <div className="col-span-2 rounded-2xl bg-[#EDEAE0] p-6">
              <MapPin size={24} className="text-[#2C3A28]" />
              <p className="mt-3 font-display text-xl text-[#2C3A28]">Find us</p>
              <p className="text-sm text-[#3B2A20]/70">{cafe.address}</p>
            </div>
          </div>
        </div>
      </section>

      {/* gallery */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <h2 className="reveal font-display text-4xl sm:text-5xl font-semibold uppercase text-[#2C3A28] mb-8">
            {c.galleryHeading || 'Inside the café'}
          </h2>
          <div className="[column-count:2] md:[column-count:3] gap-4">
            {gallery.map((src, i) => (
              <div
                key={src}
                className="reveal group mb-4 overflow-hidden rounded-3xl break-inside-avoid"
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <img
                  src={src}
                  alt="Al Naturale cafe"
                  loading="lazy"
                  className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* follow the cafe */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="reveal rounded-[2rem] bg-[#2C3A28] text-white px-8 sm:px-12 py-12 text-center grain">
            <span className="text-[#EDEAE0] text-sm font-semibold uppercase tracking-[0.2em]">
              {c.followEyebrow || 'Get Social'}
            </span>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl font-semibold uppercase text-[#E9E5D8]">
              {c.followHeading || 'Follow the café'}
            </h2>
            <p className="mt-4 text-white/80 text-lg max-w-xl mx-auto">
              {c.followBody || 'For daily specials, events and a peek inside the St Kilda café.'}
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <a
                href="https://www.instagram.com/alnaturalecafe/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cafe-instagram"
                className="inline-flex items-center gap-2.5 rounded-full bg-[#EDEAE0] text-[#2C3A28] px-7 py-3.5 font-semibold hover:bg-white transition-colors"
              >
                <Instagram size={19} /> @alnaturalecafe
              </a>
              <a
                href="https://www.facebook.com/p/Alnaturalecafe-61559516887664/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cafe-facebook"
                className="inline-flex items-center gap-2.5 rounded-full border border-white/40 text-white px-7 py-3.5 font-semibold hover:bg-white/10 transition-colors"
              >
                <Facebook size={19} /> fb.alnaturalecafe
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
